import React from 'react';
import { Square, Circle, Ellipsis, Maximize, Pencil, Ruler } from 'lucide-react';

/**
 * Компонент с основными параметрами бассейна
 */
const PoolParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  poolTypes,
  bottomSlopeTypes
}) => {
  // Получаем компонент иконки на основе ID типа бассейна
  const getPoolTypeIcon = (typeId) => {
    switch (typeId) {
      case 'rectangular': return Square;
      case 'circular': return Circle;
      case 'oval': return Ellipsis;
      case 'irregular': return Maximize;
      case 'custom': return Pencil;
      default: return Square;
    }
  };
  
  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Ruler className="w-5 h-5 mr-2 text-blue-500" />
        Параметры бассейна
      </h3>
      
      {/* Система единиц измерения */}
      <div className="flex justify-end mb-4">
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1 inline-flex">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="unitSystem"
              value="metric"
              checked={formData.unitSystem === 'metric'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 mr-2">
              Метрическая (м)
            </span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="unitSystem"
              value="imperial"
              checked={formData.unitSystem === 'imperial'}
              onChange={handleInputChange}
              className="form-radio"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Английская (фт)
            </span>
          </label>
        </div>
      </div>
      
      {/* Тип бассейна */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 mb-6">
        {poolTypes.map((type) => {
          const Icon = getPoolTypeIcon(type.id);
          return (
            <label
              key={type.id}
              className={`
                flex flex-col items-center justify-center p-4 rounded-lg border-2 
                cursor-pointer transition-colors
                ${formData.poolType === type.id 
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400' 
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}
              `}
            >
              <input
                type="radio"
                name="poolType"
                value={type.id}
                checked={formData.poolType === type.id}
                onChange={handleInputChange}
                className="sr-only"
              />
              <Icon 
                className={`w-8 h-8 mb-2 ${
                  formData.poolType === type.id 
                    ? 'text-blue-500 dark:text-blue-400' 
                    : 'text-gray-500 dark:text-gray-400'
                }`} 
              />
              <span 
                className={`text-sm font-medium ${
                  formData.poolType === type.id 
                    ? 'text-blue-700 dark:text-blue-300' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {type.name}
              </span>
            </label>
          );
        })}
      </div>
      
      {/* Размеры бассейна в зависимости от типа */}
      <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Поля для прямоугольного бассейна */}
          {formData.poolType === 'rectangular' && (
            <>
              <div>
                <label htmlFor="length" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Длина
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="length"
                    name="length"
                    value={formData.length}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.length ? 'input-error' : ''}`}
                    placeholder={`Длина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                    </span>
                  </div>
                </div>
                {errors.length && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.length}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="width" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ширина
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="width"
                    name="width"
                    value={formData.width}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.width ? 'input-error' : ''}`}
                    placeholder={`Ширина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                    </span>
                  </div>
                </div>
                {errors.width && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.width}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="depth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Глубина
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="depth"
                    name="depth"
                    value={formData.depth}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.depth ? 'input-error' : ''}`}
                    placeholder={`Глубина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                    </span>
                  </div>
                </div>
                {errors.depth && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.depth}
                  </p>
                )}
              </div>
            </>
          )}
          
          {/* Поля для круглого бассейна */}
          {formData.poolType === 'circular' && (
            <>
              <div>
                <label htmlFor="diameter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Диаметр
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="diameter"
                    name="diameter"
                    value={formData.diameter}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.diameter ? 'input-error' : ''}`}
                    placeholder={`Диаметр в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                    </span>
                  </div>
                </div>
                {errors.diameter && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.diameter}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="depth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Глубина
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="depth"
                    name="depth"
                    value={formData.depth}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.depth ? 'input-error' : ''}`}
                    placeholder={`Глубина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                    </span>
                  </div>
                </div>
                {errors.depth && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.depth}
                  </p>
                )}
              </div>
            </>
          )}
          
          {/* Поля для овального бассейна */}
          {formData.poolType === 'oval' && (
            <>
              <div>
                <label htmlFor="length" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Длина
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="length"
                    name="length"
                    value={formData.length}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.length ? 'input-error' : ''}`}
                    placeholder={`Длина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                    </span>
                  </div>
                </div>
                {errors.length && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.length}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="width" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ширина
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="width"
                    name="width"
                    value={formData.width}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.width ? 'input-error' : ''}`}
                    placeholder={`Ширина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                    </span>
                  </div>
                </div>
                {errors.width && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.width}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="depth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Глубина
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="depth"
                    name="depth"
                    value={formData.depth}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.depth ? 'input-error' : ''}`}
                    placeholder={`Глубина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                    </span>
                  </div>
                </div>
                {errors.depth && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.depth}
                  </p>
                )}
              </div>
            </>
          )}
          
          {/* Поля для нестандартной формы */}
          {formData.poolType === 'irregular' && (
            <>
              <div>
                <label htmlFor="a" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Площадь поверхности
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="a"
                    name="a"
                    value={formData.a}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.a ? 'input-error' : ''}`}
                    placeholder={`Площадь в ${formData.unitSystem === 'metric' ? 'м²' : 'фт²'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м²' : 'фт²'}
                    </span>
                  </div>
                </div>
                {errors.a && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.a}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="b" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Объем (если известен)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="b"
                    name="b"
                    value={formData.b}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.b ? 'input-error' : ''}`}
                    placeholder={`Объем в ${formData.unitSystem === 'metric' ? 'м³' : 'фт³'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м³' : 'фт³'}
                    </span>
                  </div>
                </div>
                {errors.b && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.b}
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="depth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Средняя глубина
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="depth"
                    name="depth"
                    value={formData.depth}
                    onChange={handleInputChange}
                    className={`form-input pr-12 w-full ${errors.depth ? 'input-error' : ''}`}
                    placeholder={`Глубина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                      {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                    </span>
                  </div>
                </div>
                {errors.depth && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    {errors.depth}
                  </p>
                )}
              </div>
            </>
          )}
          
          {/* Поле для типа дна */}
          <div className="md:col-span-2 xl:col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Тип дна бассейна
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {bottomSlopeTypes.map((type) => (
                <label
                  key={type.id}
                  className={`
                    relative flex flex-col p-4 rounded-lg border
                    cursor-pointer transition-colors
                    ${formData.bottomSlope === type.id 
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400' 
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}
                  `}
                >
                  <input
                    type="radio"
                    name="bottomSlope"
                    value={type.id}
                    checked={formData.bottomSlope === type.id}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <span 
                    className={`text-sm font-medium mb-1 ${
                      formData.bottomSlope === type.id 
                        ? 'text-blue-700 dark:text-blue-300' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {type.name}
                  </span>
                  <span 
                    className={`text-xs ${
                      formData.bottomSlope === type.id 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {type.description}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Средняя глубина (для дна с уклоном) */}
          {formData.bottomSlope !== 'flat' && (
            <div>
              <label htmlFor="averageDepth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Средняя глубина
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="averageDepth"
                  name="averageDepth"
                  value={formData.averageDepth}
                  onChange={handleInputChange}
                  className={`form-input pr-12 w-full ${errors.averageDepth ? 'input-error' : ''}`}
                  placeholder={`Средняя глубина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                    {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                  </span>
                </div>
              </div>
              {errors.averageDepth && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.averageDepth}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Между минимальной и максимальной глубиной
              </p>
            </div>
          )}
          
          {/* Уровень воды */}
          <div>
            <label htmlFor="waterLevel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Уровень заполнения (%)
            </label>
            <div className="flex items-center">
              <input
                type="range"
                id="waterLevel"
                name="waterLevel"
                min="50"
                max="100"
                step="1"
                value={formData.waterLevel}
                onChange={handleInputChange}
                className="form-range w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <span className="ml-3 w-12 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                {formData.waterLevel}%
              </span>
            </div>
            {errors.waterLevel && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.waterLevel}
              </p>
            )}
          </div>
          
          {/* Толщина стенок */}
          <div>
            <label htmlFor="wallThickness" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Толщина стенок
            </label>
            <div className="relative">
              <input
                type="text"
                id="wallThickness"
                name="wallThickness"
                value={formData.wallThickness}
                onChange={handleInputChange}
                className={`form-input pr-12 w-full ${errors.wallThickness ? 'input-error' : ''}`}
                placeholder={`Толщина в ${formData.unitSystem === 'metric' ? 'метрах' : 'футах'}`}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                  {formData.unitSystem === 'metric' ? 'м' : 'фт'}
                </span>
              </div>
            </div>
            {errors.wallThickness && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.wallThickness}
              </p>
            )}
          </div>
          
          {/* Площадь ступеней */}
          <div>
            <label htmlFor="stepArea" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Площадь ступеней/лестниц
            </label>
            <div className="relative">
              <input
                type="text"
                id="stepArea"
                name="stepArea"
                value={formData.stepArea}
                onChange={handleInputChange}
                className={`form-input pr-12 w-full ${errors.stepArea ? 'input-error' : ''}`}
                placeholder={`Площадь в ${formData.unitSystem === 'metric' ? 'м²' : 'фт²'}`}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                  {formData.unitSystem === 'metric' ? 'м²' : 'фт²'}
                </span>
              </div>
            </div>
            {errors.stepArea && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.stepArea}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Подсказка для более точного расчета */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
        <p className="text-sm text-blue-700 dark:text-blue-300 flex items-start">
          <svg className="w-5 h-5 text-blue-500 dark:text-blue-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            Для более точного расчета рекомендуется измерять длину и ширину по водной поверхности, а глубину - от дна до уровня воды. Учитывайте, что бассейны с нестандартной формой рассчитываются приблизительно.
          </span>
        </p>
      </div>
    </div>
  );
};

export default PoolParametersSection;